import Raven from 'raven-js';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { CardSection, CardWrapper } from 'foundations-components/transitional/components/Card';
import Url from 'urlinator/Url';
import { cancelMeeting as cancelMeetingAction, fetchEventDetails as fetchEventDetailsAction } from '../../redux/actions/BookActions';
import { track } from 'MeetingsLib/tracking/UsageTracker';
import { getMeetingsCancelStatus, getMeetingsCancelError } from '../../selectors/RequestStatusSelectors';
import { getBookInfo as getBookInfoSelector } from 'MeetingsLib/selectors/BookInfoSelectors';
import ProfileHeader from '../header/ProfileHeader';
import { STARTED, SUCCEEDED, FAILED } from 'MeetingsBase/constants/RequestStatusTypes';
import { LINK_TYPES, MEETING_TYPES } from 'MeetingsBase/constants/enums';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { getMeetingSourceFromLocation, getDisplayAvatarOption } from 'MeetingsLib/utils/utils';
import { EXCHANGE_CANCELLATION, MEETING_TEMPORARILY_UNAVAILABLE } from 'MeetingsLib/constants/errors';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function SuccessState({
  profile,
  avatar
}) {
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(ProfileHeader, {
      profile: profile,
      cancelling: true,
      booked: false,
      avatar: avatar
    }), /*#__PURE__*/_jsx(FormattedMessage, {
      message: "cancel.success.description"
    })]
  });
}
export default function CancelContainer({
  CancelMarker,
  displayInfo,
  profile
}) {
  const dispatch = useDispatch();
  useEffect(() => {
    track('meetingEventInteraction', {
      action: 'viewed cancel',
      source: getMeetingSourceFromLocation()
    });
  }, []);
  const cancelRequestStatus = useSelector(state => getMeetingsCancelStatus(state));
  const cancelRequestError = useSelector(state => getMeetingsCancelError(state));
  const isCancellingExchangeError = cancelRequestError === EXCHANGE_CANCELLATION;
  const isCancellingDuringMigrationError = cancelRequestError === MEETING_TEMPORARILY_UNAVAILABLE;

  /*  @ts-ignore Jun-18-2025, 19:10UTC fix types  */
  const slug = useSelector(state => state.userSlug);
  const meetingChangeId = useMemo(() => {
    const url = new Url(window.location);
    return url.paramValue('meetingChangeId') || url.paramValue('cancelId');
  }, []);
  useEffect(() => {
    if (isCancellingExchangeError) {
      Raven.captureMessage('Attempted to cancel Exchange meeting', {
        extra: {
          slug,
          meetingChangeId
        },
        level: 'error'
      });
    } else if (isCancellingDuringMigrationError) {
      Raven.captureMessage('Attempted to cancel meeting during active migration', {
        extra: {
          slug,
          meetingChangeId
        },
        level: 'error'
      });
    }
  }, [isCancellingExchangeError, isCancellingDuringMigrationError, meetingChangeId, slug]);

  /*  @ts-ignore Jun-18-2025, 19:10UTC fix types  */
  const eventDetails = useSelector(state => state.eventDetails);
  useEffect(() => {
    void dispatch(fetchEventDetailsAction(slug, meetingChangeId));
  }, [slug, meetingChangeId, dispatch]);
  const cancelMeeting = useCallback(() => {
    void dispatch(cancelMeetingAction(slug, meetingChangeId));
  }, [dispatch, slug, meetingChangeId]);
  const linkType = useSelector(state => getBookInfoSelector(state).linkType);
  const linkIsPersonal = linkType === LINK_TYPES.get(MEETING_TYPES.PERSONAL);
  const {
    publicDisplayAvatarOption,
    avatar,
    companyAvatar
  } = displayInfo;
  const avatarUrl = getDisplayAvatarOption(publicDisplayAvatarOption, avatar, companyAvatar, linkIsPersonal);
  const cancelRequestFailedMessageKey = isCancellingDuringMigrationError ? 'cancel.failure.alertOrEmail' : 'cancel.failure.alert';
  const cancelForm = /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(ProfileHeader, {
      profile: profile,
      booked: false,
      cancelling: true
      /*  @ts-ignore Jun-18-2025, 19:10UTC fix types  */,
      avatar: avatarUrl
    }), eventDetails && /*#__PURE__*/_jsx("p", {
      children: /*#__PURE__*/_jsx(FormattedReactMessage, {
        message: "cancel.confirm.description",
        options: {
          eventName: eventDetails.eventName,
          meetingDateTime: /*#__PURE__*/_jsx(FormattedDateTime, {
            value: eventDetails.startTime,
            format: "LLLL"
          })
        }
      })
    }), /*#__PURE__*/_jsx(LoadingButton, {
      use: "primary",
      loading: cancelRequestStatus === STARTED,
      failed: cancelRequestStatus === FAILED,
      onClick: cancelMeeting,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "cancel.confirm.cancel"
      })
    }), cancelRequestStatus === FAILED && /*#__PURE__*/_jsx(Alert, {
      use: "inline",
      type: "danger",
      marginBlockStart: "400",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: cancelRequestFailedMessageKey
      })
    })]
  });
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(CancelMarker, {}), /*#__PURE__*/_jsx(View, {
      maxInlineSize: "600px",
      marginInline: "auto",
      paddingBlockStart: "1100",
      children: /*#__PURE__*/_jsx(CardWrapper, {
        padding: "0",
        height: "100%",
        children: /*#__PURE__*/_jsx(CardSection, {
          padding: "0",
          children: /*#__PURE__*/_jsx(View, {
            marginInline: "400",
            paddingBlock: "1100",
            paddingInline: "300",
            textAlign: "center",
            children: cancelRequestStatus === SUCCEEDED ? /*#__PURE__*/_jsx(SuccessState, {
              profile: profile,
              avatar: avatarUrl
            }) : cancelForm
          })
        })
      })
    })]
  });
}